import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Header,
  Button,
  Icon,
  Card,
  CardDivider,
} from "../components/ui";
import { useStore } from "../store";
import { logScreenView, logAmountEdited } from "../services/analytics";

export function ConfirmReceiptScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const isManualEntry = location.state?.manualEntry === true;

  const { receipt, setEditedTotal, setOCRSuccess, resetReceipt, extendSession, app } = useStore();
  const { ocrResult } = receipt;

  const allowedMethods = app.venue?.allowedMethods ?? [];
  const allowOcr = allowedMethods.includes('ocr');

  // Get venue name from session (set when QR code was scanned)
  const venueNameFromSession = app.venue?.name ?? "Unknown Venue";

  // Get OCR data or null for manual entry
  const receiptData = ocrResult.status === "success" ? ocrResult.data : null;

  const [amount, setAmount] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  // Check if amount has been changed from original OCR value
  const isAmountEdited = receiptData ? parseFloat(amount) !== receiptData.total : false;

  // Initialize amount from edited total (if exists) or OCR result
  useEffect(() => {
    extendSession()
    logScreenView(isManualEntry ? 'manual_entry' : 'confirm_receipt')
    if (amount === "") {
      // Prefer editedTotal if user previously edited, otherwise use OCR total
      if (receipt.editedTotal !== null) {
        setAmount(receipt.editedTotal.toFixed(2));
      } else if (receiptData) {
        setAmount(receiptData.total.toFixed(2));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptData, receipt.editedTotal]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    setAmount(value);
  };

  const handleAmountBlur = () => {
    setIsEditing(false);
    const parsedAmount = parseFloat(amount);
    if (!isNaN(parsedAmount)) {
      setEditedTotal(parsedAmount);
    }
  };

  const handleContinue = () => {
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      return; // Don't proceed with invalid amount
    }

    if (isManualEntry) {
      // Generate tip suggestions based on entered amount (matching API format)
      const percentages = app.venue?.tipPercentages ?? [15, 18, 25];
      const tipSuggestions = percentages.map((p) => ({
        percent: p,
        amount: parsedAmount * (p / 100),
        display: `$${(parsedAmount * (p / 100)).toFixed(2)}`,
      }));

      // For manual entry, create a minimal OCR result
      setOCRSuccess({
        receiptId: `manual-${Date.now()}`,
        venue: {
          name: venueNameFromSession,
          address: "",
        },
        items: [],
        subtotal: parsedAmount,
        tax: 0,
        total: parsedAmount,
        tipIncluded: false,
        tipAmount: null,
        confidence: 1.0,
        requiresReview: false,
        tipSuggestions,
      });

      // Mark as edited so SelectTipScreen shows simplified UI and sends source: 'manual'
      setEditedTotal(parsedAmount);
    } else {
      // For OCR, save edited total if changed
      if (receiptData && parsedAmount !== receiptData.total) {
        logAmountEdited(receiptData.total, parsedAmount);
        setEditedTotal(parsedAmount);
      }
    }

    navigate("/tip");
  };

  const handleRetake = () => {
    resetReceipt();
    navigate("/capture");
  };

  // Manual entry mode - simplified UI
  if (isManualEntry) {
    return (
      <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex flex-col">
        <Header
          title="Enter Amount"
          showBack={allowOcr}
          onBack={allowOcr ? () => navigate("/capture") : undefined}
        />

        <main className="flex-1 flex flex-col px-6 py-6 overflow-y-auto">
          {/* Icon & Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
              <Icon
                name="edit"
                size={32}
                className="text-[var(--color-secondary)]"
              />
            </div>
            <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-2">
              Manual Entry
            </h2>
            <p className="text-[var(--color-text-muted)] text-sm">
              Enter your bill total below
            </p>
          </div>

          {/* Manual Entry Form */}
          <Card gradient className="mb-6">
            <div className="space-y-4">
              {/* Venue Name (from QR code session) */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <Icon
                    name="building"
                    size={20}
                    className="text-[var(--color-text-muted)]"
                  />
                </div>
                <div>
                  <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide">
                    Venue
                  </p>
                  <h3 className="font-semibold text-[var(--color-text)]">
                    {venueNameFromSession}
                  </h3>
                </div>
              </div>

              <CardDivider dashed />

              {/* Total Amount */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
                  Bill Total
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                    <span className="text-2xl font-semibold text-[var(--color-text)]">
                      $
                    </span>
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={amount}
                    onChange={handleAmountChange}
                    onFocus={(e) => {
                      setTimeout(() => {
                        e.target.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });
                      }, 100);
                    }}
                    autoFocus
                    className="
                      block w-full pl-10 pr-4 py-4
                      text-3xl font-semibold text-[var(--color-text)]
                      bg-gray-50 rounded-xl
                      border-2 border-transparent
                      focus:border-[var(--color-secondary)] focus:outline-none
                      placeholder:text-gray-300
                    "
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleContinue}
              disabled={!amount || parseFloat(amount) <= 0}
            >
              Continue to Tip
              <Icon name="arrow-right" size={20} />
            </Button>

            {allowOcr && (
              <Button variant="outline" onClick={handleRetake}>
                <Icon name="camera" size={20} />
                Scan Receipt Instead
              </Button>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Show loading if waiting for OCR data (not manual entry)
  if (!receiptData) {
    return (
      <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex flex-col">
        <Header
          title="Confirm Receipt"
          showBack
          onBack={() => navigate("/capture")}
        />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-[var(--color-text-muted)]">
            Loading receipt data...
          </p>
        </main>
      </div>
    );
  }

  // OCR mode - full receipt details
  return (
    <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex flex-col">
      <Header
        title="Confirm Receipt"
        showBack
        onBack={() => navigate("/capture")}
      />

      {/* Main Content */}
      <main className="flex-1 flex flex-col px-6 py-6 overflow-y-auto">
        {/* Icon & Title */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
            <Icon
              name="document"
              size={32}
              className="text-[var(--color-secondary)]"
            />
          </div>
          <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-2">
            Review Details
          </h2>
          <p className="text-[var(--color-text-muted)] text-sm">
            Please verify the total amount before proceeding
          </p>
        </div>

        {/* Receipt Card */}
        <Card gradient className="mb-6">
          {/* Venue Info */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <Icon
                  name="building"
                  size={20}
                  className="text-[var(--color-text-muted)]"
                />
              </div>
              <div>
                <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide">
                  Venue
                </p>
                <h3 className="font-semibold text-[var(--color-text)]">
                  {receiptData.venue.name}
                </h3>
                <p className="text-xs text-[var(--color-text-muted)]">
                  {receiptData.venue.address}
                </p>
              </div>
            </div>
            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
              <Icon
                name="check"
                size={16}
                className="text-[var(--color-success)]"
                strokeWidth={3}
              />
            </div>
          </div>

          <CardDivider dashed />

          {/* Total Amount */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
              Total Amount
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                <span className="text-2xl font-semibold text-[var(--color-text)]">
                  $
                </span>
              </div>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={handleAmountChange}
                onFocus={(e) => {
                  setIsEditing(true);
                  setTimeout(() => {
                    e.target.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                  }, 100);
                }}
                onBlur={handleAmountBlur}
                className={`
                  block w-full pl-10 pr-12 py-4
                  text-3xl font-semibold text-[var(--color-text)]
                  bg-gray-50 rounded-xl
                  border-2 transition-colors
                  ${
                    isEditing
                      ? "border-[var(--color-secondary)]"
                      : "border-transparent"
                  }
                  focus:outline-none
                `}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                <Icon
                  name="edit"
                  size={20}
                  className="text-[var(--color-text-muted)]"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-[var(--color-text-muted)] flex items-center gap-1">
              <Icon name="info" size={12} />
              Tap to edit if the amount is incorrect
            </p>
          </div>

          {/* Tax & Service Fee (hidden when amount is edited) */}
          {!isAmountEdited && (
            <div className="mt-5">
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                {receiptData.tipIncluded && receiptData.tipAmount != null && (
                  <div className="flex justify-between text-sm font-medium text-[var(--color-text)]">
                    <span>Service Fee / Other</span>
                    <span>${receiptData.tipAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-medium text-[var(--color-text)]">
                  <span>Tax</span>
                  <span>${receiptData.tax.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Spacer to push buttons to bottom */}
        <div className="flex-1" />

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button onClick={handleContinue}>
            Looks right, continue
            <Icon name="arrow-right" size={20} />
          </Button>

          <Button variant="outline" onClick={handleRetake}>
            <Icon name="camera" size={20} />
            Retake Photo
          </Button>
        </div>
      </main>
    </div>
  );
}
